import React, {useState} from 'react';
import {authorizedAPI} from 'app/services/api';
import {catchDataError} from 'app/helpers/apiHelpers';
import LivetickerAction from 'app/components/match/LivetickerAction';
import {AxiosError, AxiosResponse} from 'axios';
import {captureSentryException} from 'app/services/sentryLogging';
import {formatLongDate} from 'app/helpers/dateHelpers';
import {CreateResultResponse} from 'app/components/match/liveResult/types';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';
import {openResultForm} from 'app/actions/softTickerActions';
import {useSoftTickerContext} from 'app/hooks/useSoftTickerContext';
import {useSelector} from 'react-redux';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import {TrustLevel} from 'app/helpers/trustLevel.const';
import {ErrorWithCode} from 'app/types/ErrorWithCode';

interface ReportLiveResultProps {
  match: any;
  openAuthDialog: (title: string, subtitle: string, utmString: string) => void;
}

const getResultCreateData = (matchSlug: string) => {
  return authorizedAPI.get(`/matches/${matchSlug}/results/create`);
};

const ReportLiveResult = ({match, openAuthDialog}: ReportLiveResultProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorWithCode | null>(null);
  const {dispatch} = useSoftTickerContext();
  const {claims, fuPaUser}: any = useSelector(selectCurrentUser);

  const handleClick = () => {
    if (!fuPaUser?.id) {
      openAuthDialog(
        'Erstelle ein Konto, um ganz easy den aktuellen Spielstand melden zu können.',
        "Ob in der Halbzeitpause oder zwischendurch: Sorg' dafür, dass alle wissen, wie's bei deiner Partie steht.",
        'signup_teaser_softticker_btn'
      );
      return;
    }
    if (claims.tl && claims.tl < TrustLevel.UserWithTicker) {
      setError({
        message: 'Du musst mindestens einen Liveticker geführt haben, um für den Live-Spielstand zugelassen zu sein.',
      });
      return;
    }
    sendEventToGA4('soft_ticker_report_live_result', {
      soft_ticker_action: 'open',
      ui_view: 'page_match_index',
      match_slug: match.slug,
    });
    setIsLoading(true);
    getResultCreateData(match.slug).then(handleSuccessCallback).catch(handleErrorCallback);
  };

  const handleSuccessCallback = (response: AxiosResponse<CreateResultResponse>) => {
    dispatch(openResultForm(response.data));
    setIsLoading(false);
  };

  const handleErrorCallback = (error: AxiosError) => {
    const errorData = catchDataError(error);
    setError({message: 'Es ist ein Fehler aufgetreten, bitte versuche es später erneut.'});
    setIsLoading(false);
    if (errorData?.status >= 400 && errorData?.status < 500) {
      captureSentryException(new Error(errorData.message), {
        extra: {
          matchSlug: match?.slug,
          kickoff: formatLongDate(new Date(match?.kickoff)),
          hasTicker: match?.flags?.includes('ticker'),
        },
      });
    }
  };

  return (
    <LivetickerAction
      buttonType='secondary'
      descriptiveText='Live-Spielstand nicht mehr aktuell? 🤔'
      error={error}
      isLoading={isLoading}
      label='Live-Spielstand eintragen'
      onClick={handleClick}
    />
  );
};

export default ReportLiveResult;
